import { useEffect, useMemo, useRef, useState, type ChangeEvent, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Camera, ImagePlus, RefreshCw, Trash2 } from 'lucide-react'
import { Button } from '@quebon/ui/components/button'
import { Badge } from '@quebon/ui/components/badge'
import { Card, CardContent } from '@quebon/ui/components/card'
import { Empty, EmptyContent, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@quebon/ui/components/empty'
import { Skeleton } from '@quebon/ui/components/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@quebon/ui/components/tabs'
import { cn } from '@quebon/ui/lib/utils'
import { Alert, AlertDescription } from '@quebon/ui/components/alert'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@quebon/ui/components/dialog'
import { lcLessonPhotosApi, type LessonPhoto, type LessonPhotos } from '@/api/lcLessonPhotos'
import { lessonPhotoKey, useLessonPhotos } from '@/hooks/coaching/useLessonPhotos'
import { growthAlertKeys } from '@/hooks/coaching/useGrowthAlertPreview'
import { useAuthStore } from '@/store/authStore'
import { hasLcPermission } from '@/utils/roleUtils'
import { getApiErrorMessage } from '@/utils/apiError'
import { CoachingOperationPanel } from '../CoachingOperationPanel'
import { MAX_PHOTO_BYTES, PHOTO_ACCEPT, PhotoValidationError, formatPhotoTime, photoContentType, uploadLessonPhotos, type FailedPhoto } from './photoUpload'

export interface LessonPhotoMedia {
  count: number
  maxCount: number
  composer?: (active: boolean) => ReactNode
  pending?: boolean
  content: ReactNode
  refreshing?: boolean
  onRefresh: () => Promise<unknown>
}

interface Props {
  learnerId: number
  lessonId: number
  attemptId: number | null
  componentKey?: string | null
  componentLabel?: string
  readOnly?: boolean
  mobile?: boolean
  locked?: boolean
  lock?: () => boolean
  unlock?: () => void
  variant?: 'full' | 'upload'
  onUploaded?: () => void
  media?: LessonPhotoMedia
}
export function LessonPhotoPanel(props: Props) {
  const { learnerId, lessonId, attemptId, componentKey = null } = props
  return <ScopedLessonPhotoPanel key={JSON.stringify([learnerId, lessonId, attemptId, componentKey])} {...props} />
}

function ScopedLessonPhotoPanel({ learnerId, lessonId, attemptId, componentKey = null, componentLabel, readOnly = false, mobile = false, locked = false, lock, unlock, variant = 'full', onUploaded, media }: Props) {
  const { t, i18n } = useTranslation(['coaching', 'common'])
  const qc = useQueryClient()
  const query = useLessonPhotos(learnerId, lessonId, attemptId, true, componentKey)
  const user = useAuthStore(s => s.user)
  const [busy, setBusy] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [addMethod, setAddMethod] = useState<'photo' | 'video'>('photo')
  const [pendingPhotos, setPendingPhotos] = useState<File[]>([])
  const pendingPhotosRef = useRef<File[]>([])
  const photoLease = useRef(false)
  const unlockRef = useRef(unlock)
  unlockRef.current = unlock
  const busyRef = useRef(false)
  const [failed, setFailed] = useState<FailedPhoto[]>([])
  const [error, setError] = useState<string | null>(null)
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<LessonPhoto | null>(null)
  const [unpreviewable, setUnpreviewable] = useState<Record<string, boolean>>({})
  const camera = useRef<HTMLInputElement>(null)
  const gallery = useRef<HTMLInputElement>(null)
  const key = lessonPhotoKey(learnerId, lessonId, attemptId, componentKey)
  const title = t(media ? 'lessonPhotos.componentMediaTitle' : componentKey == null ? 'lessonPhotos.title' : 'lessonPhotos.componentTitle', { component: componentLabel ?? t('lessonPhotos.component') })
  const photos = query.data?.photos ?? []
  const selected = photos.find(photo => photo.id === selectedId) ?? null
  const editable = !readOnly && query.isSuccess && query.data.editable && hasLcPermission(user ?? undefined, 'COACHING')
  const remaining = Math.max(0, (query.data?.maxPhotos ?? 20) - photos.length)
  const tabbed = Boolean(media?.composer)
  const externallyLocked = locked && !photoLease.current
  const pendingPreviews = useMemo(() => pendingPhotos.map(file => {
    try { return { file, url: URL.createObjectURL(file) } }
    catch { return { file, url: null } }
  }), [pendingPhotos])
  useEffect(() => () => pendingPreviews.forEach(preview => { if (preview.url) URL.revokeObjectURL(preview.url) }), [pendingPreviews])
  useEffect(() => () => {
    if (photoLease.current) { photoLease.current = false; unlockRef.current?.() }
  }, [])
  useEffect(() => {
    if (pendingPhotos.length === 0) return
    const beforeUnload = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = '' }
    window.addEventListener('beforeunload', beforeUnload)
    return () => window.removeEventListener('beforeunload', beforeUnload)
  }, [pendingPhotos.length])
  const releasePhotoLease = () => {
    if (photoLease.current) { photoLease.current = false; unlock?.() }
  }
  const setPhotoDrafts = (files: File[]) => { pendingPhotosRef.current = files; setPendingPhotos(files) }
  const message = (e: unknown) => e instanceof PhotoValidationError ? t(`common:apiError.${e.code}`) : getApiErrorMessage(e, t)
  const begin = () => {
    if (!editable || busyRef.current || externallyLocked || (!photoLease.current && lock && !lock())) return false
    photoLease.current = true
    busyRef.current = true; setBusy(true); setError(null)
    return true
  }
  const end = () => { busyRef.current = false; setBusy(false); if (pendingPhotosRef.current.length === 0) releasePhotoLease() }
  const upload = async (files: File[]) => {
    if (!files.length || !begin()) return
    let savedAny = false
    try {
      const failedPhotos = await uploadLessonPhotos({ learnerId, lessonId, attemptId, componentKey, files, remaining, onSaved: photo => {
        savedAny = true
        qc.setQueryData<LessonPhotos>(key, current => current ? { ...current, photos: [...current.photos.filter(p => p.id !== photo.id), photo] } : current)
      } })
      setFailed(failedPhotos)
      if (tabbed) setPhotoDrafts(failedPhotos.map(photo => photo.file))
      if (savedAny && componentKey != null) void qc.invalidateQueries({ queryKey: growthAlertKeys.learnerPreviews(learnerId) })
      await query.refetch({ throwOnError: true })
      if (failedPhotos.length === 0) onUploaded?.()
    } catch (e) { setError(message(e)) }
    finally { end() }
  }
  const pick = (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? [])
    e.target.value = ''
    if (!tabbed) { void upload(files); return }
    if (!editable || busyRef.current || externallyLocked || files.length === 0) return
    const accepted: File[] = []
    let validationError: PhotoValidationError | null = null
    for (const file of files) {
      if (file.size > MAX_PHOTO_BYTES) { validationError ??= new PhotoValidationError('FILE_TOO_LARGE'); continue }
      if (!photoContentType(file)) { validationError ??= new PhotoValidationError('UNSUPPORTED_CONTENT_TYPE'); continue }
      if (pendingPhotosRef.current.length + accepted.length >= remaining) { validationError ??= new PhotoValidationError('PHOTO_LIMIT_EXCEEDED'); continue }
      accepted.push(file)
    }
    if (accepted.length > 0) {
      if (!photoLease.current && lock && !lock()) return
      photoLease.current = true
      setPhotoDrafts([...pendingPhotosRef.current, ...accepted])
      setFailed([])
    }
    setError(validationError ? message(validationError) : null)
  }
  const cancelPhotos = () => {
    if (busyRef.current) return
    setPhotoDrafts([]); setFailed([]); setError(null); releasePhotoLease()
  }
  const remove = async () => {
    if (!deleteTarget || !begin()) return
    try {
      await lcLessonPhotosApi.remove(learnerId, lessonId, deleteTarget.id, attemptId, componentKey)
      qc.setQueryData<LessonPhotos>(key, current => current ? { ...current, photos: current.photos.filter(p => p.id !== deleteTarget.id) } : current)
      if (componentKey != null) void qc.invalidateQueries({ queryKey: growthAlertKeys.learnerPreviews(learnerId) })
      if (selected?.id === deleteTarget.id) setSelectedId(null)
      setDeleteTarget(null)
    } catch (e) { setError(message(e)) }
    finally { end() }
  }

  const refreshMedia = async () => {
    if (busy || locked || refreshing || pendingPhotos.length > 0 || media?.pending || !media) return
    setRefreshing(true)
    setError(null)
    try { await Promise.all([query.refetch({ throwOnError: true }), media.onRefresh()]) }
    catch (e) { setError(message(e)) }
    finally { setRefreshing(false) }
  }

  const empty = variant === 'full' && query.isSuccess && photos.length === 0 && (media?.count ?? 0) === 0
  const photoSelectionLabel = t(tabbed ? 'lessonPhotos.selectPhoto' : mobile ? 'lessonPhotos.gallery' : 'lessonPhotos.chooseFiles')
  const photoControls = editable ? <div className="flex flex-col gap-3">
    <div className={cn('flex flex-wrap gap-2', empty && !tabbed && 'justify-center')}>
      {mobile ? <><input ref={camera} type="file" accept={PHOTO_ACCEPT} capture="environment" className="hidden" aria-label={t('lessonPhotos.camera')} onChange={pick} /><Button className="min-h-11" variant="default" disabled={busy || externallyLocked || remaining <= pendingPhotos.length} onClick={() => camera.current?.click()}><Camera data-icon="inline-start" />{t('lessonPhotos.camera')}</Button></> : null}
      <input ref={gallery} type="file" accept={PHOTO_ACCEPT} multiple className="hidden" aria-label={photoSelectionLabel} onChange={pick} />
      <Button className="min-h-11" variant={mobile ? 'outline' : 'default'} disabled={busy || externallyLocked || remaining <= pendingPhotos.length} onClick={() => gallery.current?.click()}><ImagePlus data-icon="inline-start" />{photoSelectionLabel}</Button>
    </div>
    {pendingPhotos.length > 0 ? <section aria-label={t('lessonPhotos.selectedPhotos')} className="flex flex-col gap-3">
      <p className="text-sm font-medium">{t('lessonPhotos.selectedPhotos')}</p>
      <ul className="grid grid-cols-2 gap-3">{pendingPreviews.map((preview, index) => <li key={`${preview.file.name}:${index}`} className="flex min-w-0 flex-col gap-1">
        {preview.url && !unpreviewable[preview.url] ? <img src={preview.url} alt={preview.file.name} className="aspect-square w-full rounded-lg object-contain" onError={() => setUnpreviewable(current => ({ ...current, [preview.url ?? '']: true }))} /> : <p className="text-xs text-muted-foreground">{t('lessonPhotos.previewUnavailable')}</p>}
        <p className="break-words text-xs text-muted-foreground">{preview.file.name}</p>
      </li>)}</ul>
      <div className="flex flex-wrap gap-2">
        <Button className="min-h-11" disabled={busy || externallyLocked} onClick={() => void upload(pendingPhotosRef.current)}>{busy ? t('lessonPhotos.working') : t('lessonPhotos.uploadSelected')}</Button>
        <Button className="min-h-11" variant="outline" disabled={busy} onClick={cancelPhotos}>{t('lessonPhotos.cancelSelection')}</Button>
      </div>
    </section> : null}
  </div> : null
  const methodsLocked = busy || locked || pendingPhotos.length > 0 || media?.pending === true
  const uploadActions = editable || media ? <div className="flex flex-col gap-3">
    {tabbed ? <Tabs value={addMethod} onValueChange={value => { if (!methodsLocked && (value === 'photo' || value === 'video')) setAddMethod(value) }}>
      <TabsList className="min-h-11 w-full" aria-label={t('attachmentType', { ns: 'video' })}>
        <TabsTrigger value="photo" className="min-h-11" disabled={methodsLocked}>{t('photo', { ns: 'video' })}</TabsTrigger>
        <TabsTrigger value="video" className="min-h-11" disabled={methodsLocked}>{t('video', { ns: 'video' })}</TabsTrigger>
      </TabsList>
      <TabsContent value="photo" keepMounted>{photoControls}</TabsContent>
      <TabsContent value="video" keepMounted>{media?.composer?.(addMethod === 'video')}</TabsContent>
    </Tabs> : photoControls}
    {media ? <Button variant="outline" className="min-h-11 self-start" disabled={busy || locked || refreshing || media.refreshing || pendingPhotos.length > 0 || media.pending} onClick={() => void refreshMedia()}><RefreshCw data-icon="inline-start" />{t('buildup.media.refresh')}</Button> : null}
    {editable || media?.composer ? <p className="text-sm text-muted-foreground">{media ? t('lessonPhotos.mediaLimits', { photos: query.data?.maxPhotos ?? 20, videos: media.maxCount }) : t('lessonPhotos.limits')}</p> : null}
  </div> : null
  const body = <>
    {query.isPending ? <div className="flex flex-col gap-3" role="status">
      <p className="text-sm text-muted-foreground">{t('dashboard.loading')}</p>
      {variant === 'full' ? <div className="grid grid-cols-2 gap-3 sm:grid-cols-3" aria-hidden="true">{[0, 1].map(index => <Skeleton key={index} className="aspect-square w-full" />)}</div> : null}
    </div> : null}
    {query.error || error ? <Alert variant="destructive"><AlertDescription className="flex flex-wrap items-center justify-between gap-2"><span>{error ?? message(query.error)}</span><Button variant="outline" className="min-h-11" disabled={busy} onClick={() => { setError(null); void query.refetch() }}>{t('roster.retry')}</Button></AlertDescription></Alert> : null}
    {variant === 'upload' || tabbed ? uploadActions : !empty && (editable || media) ? <div className="flex flex-col gap-3 rounded-lg border border-border bg-muted/30 p-3">
      {editable || media?.composer ? <p className="text-sm font-medium">{t(media ? 'lessonPhotos.addMedia' : 'lessonPhotos.addPhotos')}</p> : null}
      {uploadActions}
    </div> : null}
    {variant === 'upload' && query.isSuccess && !editable ? <p className="text-sm text-muted-foreground">{t('lessonPhotos.readOnly')}</p> : null}
    {busy ? <p role="status">{t('lessonPhotos.working')}</p> : null}
    {failed.length > 0 ? <Alert variant="destructive"><AlertDescription className="flex flex-col gap-3"><ul className="flex flex-col gap-1">{failed.map((f, i) => <li key={i} className="break-words">{f.file.name}: {message(f.error)}</li>)}</ul>
      <Button className="min-h-11 self-start" variant="outline" disabled={busy || externallyLocked || !editable || remaining === 0} onClick={() => void upload(failed.map(f => f.file))}>{t('lessonPhotos.retryFailed')}</Button>
    </AlertDescription></Alert> : null}
    {empty && (!tabbed || (pendingPhotos.length === 0 && !media?.pending)) ? <Empty>
      <EmptyHeader>
        <EmptyMedia variant="icon"><ImagePlus aria-hidden="true" /></EmptyMedia>
        <EmptyTitle>{t(media ? 'lessonPhotos.mediaEmpty' : componentKey == null ? 'lessonPhotos.empty' : 'lessonPhotos.componentEmpty')}</EmptyTitle>
        {editable || media?.composer ? <EmptyDescription>{t(media ? 'lessonPhotos.mediaEmptyGuide' : 'lessonPhotos.emptyGuide')}</EmptyDescription> : null}
      </EmptyHeader>
      {!tabbed && (editable || media) ? <EmptyContent>{uploadActions}</EmptyContent> : null}
    </Empty> : null}
    {variant === 'full' && photos.length > 0 ? <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3">{photos.map((photo, index) => <li key={photo.id} className="min-w-0">
      <Card size="sm" className="h-full min-w-0">
        <CardContent className="flex min-w-0 flex-col gap-3">
          <Button variant="outline" className="h-auto min-h-28 w-full overflow-hidden p-1" onClick={() => setSelectedId(photo.id)} aria-label={t('lessonPhotos.open', { n: index + 1 })}>
            {unpreviewable[photo.photoUrl] ? <span className="whitespace-normal p-2">{t('lessonPhotos.previewUnavailable')}</span> : <img src={photo.photoUrl} alt={t(componentKey == null ? 'lessonPhotos.alt' : 'lessonPhotos.componentAlt', { n: index + 1, component: componentLabel ?? t('lessonPhotos.component') })} className="aspect-square w-full object-cover" onError={() => setUnpreviewable(s => ({ ...s, [photo.photoUrl]: true }))} />}
          </Button>
          <div className="flex min-w-0 flex-col gap-1">
            <p className="text-sm font-medium">{t('lessonPhotos.photoNumber', { n: index + 1 })}</p>
            <p className="break-words text-xs text-muted-foreground">{photo.uploadedByName ?? t('lessonPhotos.teacher')}</p>
            <p className="break-words text-xs tabular-nums text-muted-foreground">{formatPhotoTime(photo.uploadedAt, i18n.language)}</p>
          </div>
          {editable ? <Button variant="destructive" size="sm" className="min-h-11 w-full" disabled={busy || locked} onClick={() => setDeleteTarget(photo)} aria-label={t('lessonPhotos.deletePhoto', { n: index + 1 })}><Trash2 data-icon="inline-start" />{t('lessonPhotos.delete')}</Button> : null}
        </CardContent>
      </Card>
    </li>)}</ul> : null}
    {variant === 'full' ? media?.content : null}
    <Dialog open={selected != null} onOpenChange={open => { if (!open) setSelectedId(null) }}>
      <DialogContent className="h-[calc(100dvh-2rem)] sm:max-w-[calc(100vw-2rem)]" showCloseButton={false}>
        <DialogHeader><DialogTitle>{title}</DialogTitle><DialogDescription>{selected ? formatPhotoTime(selected.uploadedAt, i18n.language) : ''}</DialogDescription></DialogHeader>
        {selected ? unpreviewable[selected.photoUrl] ? <p>{t('lessonPhotos.previewUnavailable')}</p> : <img src={selected.photoUrl} alt={title} className="min-h-0 max-h-[70dvh] w-full object-contain" /> : null}
        <DialogFooter><Button variant="outline" className="min-h-11" onClick={() => setSelectedId(null)}>{t('lessonPhotos.close')}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
    <Dialog open={deleteTarget != null} onOpenChange={open => { if (!open && !busy) setDeleteTarget(null) }}>
      <DialogContent showCloseButton={false}><DialogHeader><DialogTitle>{t('lessonPhotos.delete')}</DialogTitle><DialogDescription>{t('lessonPhotos.deleteConfirm')}</DialogDescription></DialogHeader>
        {error ? <p role="alert" className="text-destructive">{error}</p> : null}
        <DialogFooter><Button variant="outline" className="min-h-11" disabled={busy} onClick={() => setDeleteTarget(null)}>{t('sessionControl.cancel')}</Button><Button variant="destructive" className="min-h-11" disabled={busy || locked || !editable} onClick={() => void remove()}>{t('lessonPhotos.delete')}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  </>
  const description = t(media ? 'lessonPhotos.componentMediaDescription' : componentKey == null ? 'lessonPhotos.description' : 'lessonPhotos.componentDescription')
  if (variant === 'upload') return <section className="flex flex-col gap-4" aria-label={title}>
    <div className="flex flex-col gap-1">
      <p className="text-sm font-medium">{componentLabel ?? title}</p>
      <p className="text-sm text-muted-foreground">{description}</p>
    </div>
    {body}
  </section>
  return <CoachingOperationPanel kind="lessonPhoto" title={title} description={description} bodyClassName="flex flex-col gap-4 p-4" actions={query.isSuccess ? <div className="flex flex-wrap items-center gap-2">
    <Badge variant="outline">{media ? t('lessonPhotos.mediaCount', { photos: photos.length, maxPhotos: query.data.maxPhotos, videos: media.count, maxVideos: media.maxCount }) : t('lessonPhotos.count', { count: photos.length, max: query.data.maxPhotos })}</Badge>
    {!editable ? <Badge variant="secondary">{t('lessonPhotos.readOnly')}</Badge> : null}
  </div> : undefined}>
    {body}
  </CoachingOperationPanel>
}
